import { useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, LogOut, Lock, Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { admin, getToken, setToken } from "@/lib/api";
import { inputCls } from "@/admin/ui";
import BlogTab from "@/admin/tabs/BlogTab";
import ResourcesTab from "@/admin/tabs/ResourcesTab";
import TestimonialsTab from "@/admin/tabs/TestimonialsTab";
import FaqsTab from "@/admin/tabs/FaqsTab";
import ServicesTab from "@/admin/tabs/ServicesTab";
import InquiriesTab from "@/admin/tabs/InquiriesTab";
import SubscribersTab from "@/admin/tabs/SubscribersTab";
import SettingsTab from "@/admin/tabs/SettingsTab";
import SeoTab from "@/admin/tabs/SeoTab";

const TABS = [
  { id: "posts", label: "Blog Posts", component: BlogTab },
  { id: "resources", label: "Resources", component: ResourcesTab },
  { id: "testimonials", label: "Testimonials", component: TestimonialsTab },
  { id: "faqs", label: "FAQs", component: FaqsTab },
  { id: "services", label: "Services", component: ServicesTab },
  { id: "inquiries", label: "Inquiries", component: InquiriesTab },
  { id: "subscribers", label: "Subscribers", component: SubscribersTab },
  { id: "settings", label: "Announcement & Buttons", component: SettingsTab },
  { id: "seo", label: "SEO", component: SeoTab },
];

function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await admin.login(password);
      onLogin();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-5" data-testid="admin-login">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-3xl border border-border bg-card p-10 text-center shadow-soft"
      >
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-forest/10 text-forest dark:bg-terracotta-500/15 dark:text-terracotta-400">
          <Lock className="h-6 w-6" />
        </span>
        <h1 className="mt-6 font-heading text-2xl font-semibold text-foreground">Admin Dashboard</h1>
        <p className="mt-2 text-sm text-muted-foreground">Evol &amp; Troppus — owner access only.</p>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Admin password"
          aria-label="Admin password"
          data-testid="admin-password-input"
          className={`${inputCls} mt-7 text-center`}
          autoFocus
        />
        {error && (
          <p className="mt-3 text-xs text-destructive" data-testid="admin-login-error">{error}</p>
        )}
        <button
          type="submit"
          disabled={loading || !password}
          data-testid="admin-login-button"
          className="btn-primary mt-5 w-full justify-center disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {loading ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </div>
  );
}

export default function AdminApp() {
  const [authed, setAuthed] = useState(!!getToken());
  const [tab, setTab] = useState("posts");

  if (!authed) return <Login onLogin={() => setAuthed(true)} />;

  const Active = TABS.find((t) => t.id === tab).component;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="admin-dashboard">
      <header className="glass-header sticky top-0 z-40 border-b border-border">
        <div className="container-site flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo compact />
            <span className="font-heading text-base font-semibold text-foreground">Admin</span>
          </div>
          <div className="flex items-center gap-2.5">
            <Link to="/" data-testid="admin-view-site" className="btn-secondary !px-4 !py-2 !text-xs">
              <ExternalLink className="h-3.5 w-3.5" /> View site
            </Link>
            <button
              onClick={() => { setToken(null); setAuthed(false); }}
              data-testid="admin-logout-button"
              className="btn-secondary !px-4 !py-2 !text-xs"
            >
              <LogOut className="h-3.5 w-3.5" /> Log out
            </button>
          </div>
        </div>
      </header>

      <div className="container-site flex flex-col gap-8 py-10 lg:flex-row">
        <aside className="shrink-0 lg:w-56">
          <nav className="flex gap-1 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible" data-testid="admin-nav">
            {TABS.map((t) => (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`whitespace-nowrap rounded-xl px-4 py-2.5 text-left text-sm font-medium transition-colors duration-300 ${
                  tab === t.id
                    ? "bg-forest text-cream dark:bg-terracotta-500"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </aside>
        <section className="min-w-0 flex-1">
          <Active />
        </section>
      </div>
    </div>
  );
}
